import { useCallback, useEffect, useReducer } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  getImportSessionDirect,
  createImportSessionDirect,
  updateImportSessionDirect,
  acquireSessionLockDirect,
  releaseSessionLockDirect,
  abandonSessionDirect,
} from '@timetable/api';
import type {
  ImportSession,
  ParsedMasterData,
  TimetableRow,
  TimetableFormat,
  MasterDataValidationReport,
  ImportResult,
} from '@timetable/types';
import { useAuthStore } from '@/store/authStore';
import { WizardStepIndicator } from './WizardStepIndicator';
import { SessionResumeBanner } from './SessionResumeBanner';
import { SessionLockedScreen } from './SessionLockedScreen';
import { Step3AcademicYear } from './steps/Step3AcademicYear';
import { Step1MasterData } from './steps/Step1MasterData';
import { Step2TimetableDecision } from './steps/Step2TimetableDecision';
import { Step4TimetableFormat } from './steps/Step4TimetableFormat';
import { Step5TimetableUpload } from './steps/Step5TimetableUpload';
import { Step6Confirm } from './steps/Step6Confirm';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface WizardState {
  session: ImportSession | null;
  currentStep: number;
  // Step 1
  selectedAcademicYear: string;
  validFrom: string;
  validTo: string;
  // Step 2
  masterDataFile: File | null;
  parsedMasterData: ParsedMasterData | null;
  masterDataValidation: MasterDataValidationReport | null;
  // Step 3
  includeTimetable: boolean | null;
  // Step 4
  timetableFormat: TimetableFormat | null;
  // Step 5
  timetableFiles: File[];
  parsedTimetableRows: TimetableRow[] | null;
  timetableValidation: MasterDataValidationReport | null;
  // Step 6
  importResult: ImportResult | null;
  // UI
  loading: boolean;
  error: string | null;
  /** null = not yet checked, 'resume' = session found, 'locked' = locked by another user, 'new' = no session */
  sessionState: null | 'resume' | 'locked' | 'new' | 'active';
}

const initialState: WizardState = {
  session: null,
  currentStep: 1,
  selectedAcademicYear: '',
  validFrom: '',
  validTo: '',
  masterDataFile: null,
  parsedMasterData: null,
  masterDataValidation: null,
  includeTimetable: null,
  timetableFormat: null,
  timetableFiles: [],
  parsedTimetableRows: null,
  timetableValidation: null,
  importResult: null,
  loading: true,
  error: null,
  sessionState: null,
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type WizardAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SESSION_LOADED'; payload: { session: ImportSession; state: 'resume' | 'locked' } }
  | { type: 'NO_SESSION' }
  | { type: 'RESUME' }
  | { type: 'START_OVER' }
  | { type: 'LOCK_EXPIRED' }
  | { type: 'SESSION_CREATED'; payload: ImportSession }
  | { type: 'SESSION_UPDATED'; payload: ImportSession }
  | { type: 'SET_STEP'; payload: number }
  | { type: 'SET_ACADEMIC_YEAR'; payload: string }
  | { type: 'SET_DATE_RANGE'; payload: { validFrom: string; validTo: string } }
  | { type: 'SET_MASTER_DATA'; payload: { file: File; parsed: ParsedMasterData; validation: MasterDataValidationReport } }
  | { type: 'SET_INCLUDE_TIMETABLE'; payload: boolean }
  | { type: 'SET_TIMETABLE_FORMAT'; payload: TimetableFormat }
  | { type: 'SET_TIMETABLE_UPLOAD'; payload: { files: File[]; rows: TimetableRow[]; validation: MasterDataValidationReport } }
  | { type: 'SET_IMPORT_RESULT'; payload: ImportResult };

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SESSION_LOADED':
      return {
        ...state,
        session: action.payload.session,
        sessionState: action.payload.state,
        currentStep: action.payload.session.current_step,
        selectedAcademicYear: action.payload.session.academic_year,
        validFrom: action.payload.session.date_range_from ?? '',
        validTo: action.payload.session.date_range_to ?? '',
        includeTimetable: action.payload.session.include_timetable,
        timetableFormat: action.payload.session.timetable_format,
        parsedMasterData: action.payload.session.master_data_json,
        parsedTimetableRows: action.payload.session.timetable_json,
        loading: false,
      };
    case 'NO_SESSION':
      return { ...state, sessionState: 'new', loading: false };
    case 'RESUME':
      return { ...state, sessionState: 'active' };
    case 'START_OVER':
      return { ...initialState, loading: false, sessionState: 'new' };
    case 'LOCK_EXPIRED':
      return { ...state, sessionState: 'resume' };
    case 'SESSION_CREATED':
      return { ...state, session: action.payload, sessionState: 'active', loading: false };
    case 'SESSION_UPDATED':
      return { ...state, session: action.payload };
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_ACADEMIC_YEAR':
      return { ...state, selectedAcademicYear: action.payload };
    case 'SET_DATE_RANGE':
      return { ...state, validFrom: action.payload.validFrom, validTo: action.payload.validTo };
    case 'SET_MASTER_DATA':
      return {
        ...state,
        masterDataFile: action.payload.file,
        parsedMasterData: action.payload.parsed,
        masterDataValidation: action.payload.validation,
      };
    case 'SET_INCLUDE_TIMETABLE':
      return { ...state, includeTimetable: action.payload };
    case 'SET_TIMETABLE_FORMAT':
      return { ...state, timetableFormat: action.payload };
    case 'SET_TIMETABLE_UPLOAD':
      return {
        ...state,
        timetableFiles: action.payload.files,
        parsedTimetableRows: action.payload.rows,
        timetableValidation: action.payload.validation,
      };
    case 'SET_IMPORT_RESULT':
      return { ...state, importResult: action.payload };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ImportWizard() {
  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const { user, schoolContext } = useAuthStore();

  // Load session on mount
  useEffect(() => {
    if (!schoolContext) {
      dispatch({ type: 'NO_SESSION' });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const session = await getImportSessionDirect(schoolContext);
        if (cancelled) return;
        if (!session) {
          dispatch({ type: 'NO_SESSION' });
          return;
        }
        // Determine if locked by someone else
        const isLockedByOther =
          session.locked_by &&
          session.locked_by !== user?.id &&
          session.locked_at &&
          Date.now() - new Date(session.locked_at).getTime() < 15 * 60 * 1000;

        dispatch({
          type: 'SESSION_LOADED',
          payload: { session, state: isLockedByOther ? 'locked' : 'resume' },
        });
      } catch {
        if (!cancelled) dispatch({ type: 'NO_SESSION' });
      }
    })();

    return () => { cancelled = true; };
  }, [schoolContext, user?.id]);

  // Release lock on unmount
  useEffect(() => {
    return () => {
      const { session } = state;
      if (session && user?.id && session.locked_by === user.id) {
        releaseSessionLockDirect(session.id, user.id).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResume = useCallback(async () => {
    const { session } = state;
    if (!session || !user?.id) return;
    try {
      const locked = await acquireSessionLockDirect(session.id, user.id);
      dispatch({ type: 'SESSION_UPDATED', payload: locked });
      dispatch({ type: 'RESUME' });
    } catch (err: unknown) {
      dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : 'Failed to acquire lock' });
    }
  }, [state, user?.id]);

  const handleStartOver = useCallback(async () => {
    const { session } = state;
    if (session && user?.id) {
      try {
        await abandonSessionDirect(session.id, user.id);
      } catch {
        // best-effort
      }
    }
    dispatch({ type: 'START_OVER' });
  }, [state, user?.id]);

  const handleLockExpired = useCallback(() => {
    dispatch({ type: 'LOCK_EXPIRED' });
  }, []);

  const advanceStep = useCallback(
    async (nextStep: number, patch: Partial<Parameters<typeof updateImportSessionDirect>[1]> = {}) => {
      const { session } = state;
      if (!session || !user?.id) return;
      try {
        const updated = await updateImportSessionDirect(session.id, {
          ...patch,
          current_step: nextStep,
          updated_by: user.id,
        });
        dispatch({ type: 'SESSION_UPDATED', payload: updated });
        dispatch({ type: 'SET_STEP', payload: nextStep });
      } catch (err: unknown) {
        dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : 'Failed to save progress' });
      }
    },
    [state, user?.id]
  );

  const startNewSession = useCallback(async () => {
    if (!schoolContext || !user?.id) return;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const session = await createImportSessionDirect({
        school_id: schoolContext,
        academic_year: '',
        started_by: user.id,
      });
      const locked = await acquireSessionLockDirect(session.id, user.id);
      dispatch({ type: 'SESSION_CREATED', payload: locked });
    } catch (err: unknown) {
      dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : 'Failed to create session' });
    }
  }, [schoolContext, user?.id]);

  // These must be declared before any early returns to satisfy Rules of Hooks
  // New step order:
  //   1 = Master Data
  //   2 = Include Timetable?
  //   3 = Academic Year (only when includeTimetable=true)
  //   4 = Timetable Format
  //   5 = Upload Timetable
  //   6 = Confirm
  const goBack = useCallback(() => {
    const { currentStep, includeTimetable } = state;
    if (currentStep === 4) {
      // Back from Format → Academic Year (step 3)
      dispatch({ type: 'SET_STEP', payload: 3 });
    } else if (currentStep === 6 && includeTimetable === false) {
      // Back from Confirm (master-data-only) → Timetable Decision (step 2)
      dispatch({ type: 'SET_STEP', payload: 2 });
    } else {
      dispatch({ type: 'SET_STEP', payload: currentStep - 1 });
    }
  }, [state]);

  const handleStartNew = useCallback(async () => {
    const { session } = state;
    if (session && user?.id) {
      try { await abandonSessionDirect(session.id, user.id); } catch { /* best-effort */ }
    }
    dispatch({ type: 'START_OVER' });
  }, [state, user?.id]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (state.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="error">{state.error}</Typography>
      </Box>
    );
  }

  // Session locked by another user
  if (state.sessionState === 'locked' && state.session) {
    return (
      <SessionLockedScreen
        session={state.session}
        onLockExpired={handleLockExpired}
      />
    );
  }

  // Existing session — show resume banner before entering wizard
  if (state.sessionState === 'resume' && state.session) {
    return (
      <Box>
        <SessionResumeBanner
          session={state.session}
          onResume={handleResume}
          onStartOver={handleStartOver}
        />
      </Box>
    );
  }

  // No session yet — prompt to start
  if (state.sessionState === 'new') {
    return (
      <Box
        sx={{
          py: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 32,
          }}
        >
          📥
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            No active import session
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start a new import to bulk-load teachers, subjects, classes, class schedules, and timetable data.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={startNewSession}
          sx={{ px: 4, py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Start New Import
        </Button>
      </Box>
    );
  }

  // Active wizard
  return (
    <Box>
      <WizardStepIndicator currentStep={state.currentStep} includeTimetable={state.includeTimetable} />
      <Box sx={{ px: 2, py: 3 }}>
        {/* Step 1: Master Data (no academic year needed) */}
        {state.currentStep === 1 && (
          <Step1MasterData state={state} dispatch={dispatch} onNext={(patch) => advanceStep(2, patch)} onBack={goBack} />
        )}
        {/* Step 2: Include Timetable? */}
        {state.currentStep === 2 && (
          <Step2TimetableDecision
            state={state}
            dispatch={dispatch}
            onNext={async (patch) => {
              const includeTimetable = (patch as any)?.include_timetable ?? state.includeTimetable;
              // If yes → go to Academic Year (step 3); if no → skip to Confirm (step 6)
              await advanceStep(includeTimetable ? 3 : 6, patch);
            }}
            onBack={goBack}
          />
        )}
        {/* Step 3: Academic Year (only when including timetable) */}
        {state.currentStep === 3 && (
          <Step3AcademicYear state={state} dispatch={dispatch} onNext={(patch) => advanceStep(4, patch)} onBack={goBack} />
        )}
        {/* Step 4: Timetable Format */}
        {state.currentStep === 4 && (
          <Step4TimetableFormat state={state} dispatch={dispatch} onNext={(patch) => advanceStep(5, patch)} onBack={goBack} />
        )}
        {/* Step 5: Upload Timetable */}
        {state.currentStep === 5 && (
          <Step5TimetableUpload state={state} dispatch={dispatch} onNext={(patch) => advanceStep(6, patch)} onBack={goBack} />
        )}
        {/* Step 6: Confirm */}
        {state.currentStep === 6 && (
          <Step6Confirm state={state} dispatch={dispatch} onBack={goBack} onStartNew={handleStartNew} />
        )}
      </Box>
    </Box>
  );
}

// Export state/action types for step components
export type { WizardAction };
